import Box from '@mui/material/Box';
import { useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { RoiId } from '~/domain/changes/ids';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import StructureDeletionConfirmationDialog from './StructureDeletionConfirmationDialog';
import StructureProperties from './StructureProperties';

export default function StructureLayer({
    roi,
    roiId,
    isMultiSelected = false,
    onSelectionInput,
}: {
    roi: RoiReadModel;
    roiId?: RoiId;
    isMultiSelected?: boolean;
    onSelectionInput?: (
        roiNumber: number,
        mods: { ctrl: boolean; shift: boolean }
    ) => boolean;
}) {
    const roiFieldRef = useRef<HTMLDivElement | null>(null);
    const selectedROI = useRecoilValue<number>(selectedROIState);
    const ROINumber = roi.roiNumber;
    const isSelected = selectedROI === ROINumber;
    const structureCanBeDeleted =
        roi.approvalStatus !== DICOMApprovalStatus.APPROVED;

    return (
        <Box
            ref={roiFieldRef}
            display="flex"
            alignItems="center"
            data-structure-row
            data-roi-number={ROINumber}
            role="option"
            aria-selected={isSelected || isMultiSelected}
            sx={(theme) => ({
                position: 'relative',
                background: isMultiSelected
                    ? theme.palette.action.selected
                    : isSelected
                      ? theme.palette.primary.contrastText
                      : 'none',
                '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                },
                // Left bar marks the active ROI only, so an active row that is
                // also a bulk member reads as in-scope (bar + selected tint).
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 2,
                    bgcolor: isSelected ? 'primary.main' : 'transparent',
                    transition: 'background-color 0.15s ease-in-out',
                },
            })}
        >
            <StructureProperties
                roi={roi}
                roiId={roiId}
                onSelectionInput={onSelectionInput}
            />
            {structureCanBeDeleted && (
                <StructureDeletionConfirmationDialog
                    roi={roi}
                    roiId={roiId}
                    isSelected={isSelected}
                />
            )}
        </Box>
    );
}
